import { getSessionId } from './session';
import { BASE, getCsrfToken } from '../api';

const STORAGE_KEY = 'greatbreak_activity_events';
const FLUSH_INTERVAL = 5000;
const MAX_BUFFER = 50;
const MAX_BATCH = 100;

const SURFACES = new Set([
  'global', 'auth', 'reels', 'search', 'planner', 'chat', 'content', 'booking', 'subscription',
]);
const EVENT_TYPES = new Set([
  'screen_view', 'session_start', 'signup_started', 'signup_completed', 'login_completed',
  'reel_view', 'reel_impression', 'reel_watch_50', 'reel_watch_90', 'reel_like', 'reel_share',
  'reel_skip', 'reel_click_book', 'search_submitted', 'search_result_clicked',
  'trip_plan_generated', 'plan_step_completed', 'item_saved', 'chat_message_sent',
  'chat_response_received', 'promo_opened', 'creator_profile_viewed', 'blog_published',
  'booking_click', 'checkout_started', 'subscription_started', 'subscription_completed',
]);

let eventBuffer = [];
let flushTimer = null;
let flushInFlight = false;
let listenersBound = false;
let lastBeaconedKeys = null;

function loadQueued() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveQueued(events) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {}
}

function eventKey(event) {
  return JSON.stringify(event);
}

function mergeIntoQueue(events) {
  const current = loadQueued();
  const have = new Set(current.map(eventKey));
  saveQueued([...current, ...events.filter(e => !have.has(eventKey(e)))]);
}

function queueAfterSend(toSend, remainder) {
  const sentKeys = new Set(toSend.map(eventKey));
  const current = loadQueued().filter(e => !sentKeys.has(eventKey(e)));
  const have = new Set(current.map(eventKey));
  saveQueued([...current, ...remainder.filter(e => !have.has(eventKey(e)))]);
}

function getApiBase() {
  return BASE;
}

function ensureTimer() {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL);
}

function clearTimer() {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function rearmIfNeeded() {
  if (listenersBound || eventBuffer.length > 0 || loadQueued().length > 0) {
    ensureTimer();
  }
}

async function sendBatch(events, { beacon = false } = {}) {
  const url = `${getApiBase()}/activity/events`;
  const body = JSON.stringify({ events });
  const csrf = getCsrfToken();

  // sendBeacon can't carry the CSRF header, so logged-in sessions use keepalive fetch.
  if (beacon && !csrf) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return true;
    } catch {}
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      body,
      credentials: 'include',
      ...(beacon ? { keepalive: true } : {}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function flush({ beacon = false } = {}) {
  clearTimer();

  if (!beacon && flushInFlight) {
    rearmIfNeeded();
    return;
  }

  const queued = loadQueued();
  if (eventBuffer.length === 0 && queued.length === 0) {
    rearmIfNeeded();
    return;
  }

  const batch = [...queued, ...eventBuffer.splice(0, eventBuffer.length)];
  const toSend = batch.slice(0, MAX_BATCH);
  const remainder = batch.slice(MAX_BATCH);

  if (beacon) {
    mergeIntoQueue(batch);
    const keys = toSend.map(eventKey);
    const alreadyBeaconed = lastBeaconedKeys !== null
      && keys.length === lastBeaconedKeys.length
      && keys.every((k, i) => k === lastBeaconedKeys[i]);
    if (!alreadyBeaconed) {
      lastBeaconedKeys = keys;
      await sendBatch(toSend, { beacon: true });
    }
    return;
  }

  flushInFlight = true;
  try {
    const ok = await sendBatch(toSend);
    if (ok) {
      queueAfterSend(toSend, remainder);
    } else {
      mergeIntoQueue(batch);
    }
  } finally {
    flushInFlight = false;
  }
  rearmIfNeeded();
}

function onBeforeUnload() {
  flush({ beacon: true });
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') flush({ beacon: true });
}

export function trackActivity(surface, eventType, properties = {}) {
  const validSurface = typeof surface === 'string' && SURFACES.has(surface);
  const validType = typeof eventType === 'string' && EVENT_TYPES.has(eventType);
  if (!validSurface || !validType) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[activity] dropped invalid event (surface=${String(surface)}, eventType=${String(eventType)})`);
    }
    return;
  }

  const sessionId = getSessionId();
  const { path, watchMs, sessionId: _ignored, ...rest } = properties || {};

  const event = {
    surface,
    eventType,
    ...(sessionId ? { sessionId } : {}),
    ...(typeof path === 'string' ? { path: path.slice(0, 300) } : {}),
    ...(Number.isInteger(watchMs) && watchMs > 0 ? { watchMs } : {}),
  };

  const payload = rest && Object.keys(rest).length > 0 ? rest : null;
  if (payload) {
    event.payload = payload;
  }

  eventBuffer.push(event);
  ensureTimer();
  if (eventBuffer.length >= MAX_BUFFER) {
    flush();
  }
}

export function trackScreen(screenName) {
  trackActivity('global', 'screen_view', { screen: screenName, path: window.location.pathname });
}

export function bindSession(sessionId) {
  try {
    const url = `${getApiBase()}/activity/bind`;
    const sid = sessionId !== undefined && sessionId !== null ? sessionId : getSessionId();
    if (!sid) return;
    const csrf = getCsrfToken();
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
      body: JSON.stringify({ sessionId: sid }),
      credentials: 'include',
    }).catch(() => {});
  } catch {
    // ignore
  }
}

export function flushActivityEvents() {
  return flush();
}

export function startActivityTracking() {
  if (!listenersBound) {
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);
    listenersBound = true;
  }
  ensureTimer();
}

export function stopActivityTracking() {
  if (listenersBound) {
    window.removeEventListener('beforeunload', onBeforeUnload);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    listenersBound = false;
  }
  clearTimer();
}
